import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertTriangle, ArrowLeft, CheckCircle, Edit } from "lucide-react";

export default function ValidationPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchValidationIssues();
  }, []);

  const fetchValidationIssues = async () => {
    try {
      const response = await axios.get(`${API}/articles/validation-issues`);
      setIssues(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Validierung");
    } finally {
      setLoading(false);
    }
  };

  const getIssueColor = (issuesCount) => {
    if (issuesCount >= 4) return "bg-red-100 text-red-700";
    if (issuesCount >= 2) return "bg-orange-100 text-orange-700";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                size="icon"
                data-testid="back-button"
                className="border-slate-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Fehler überprüfen</h1>
                  <p className="text-sm text-slate-600">
                    {issues.length === 0 ? "Keine Probleme gefunden" : `${issues.length} Artikel mit Problemen`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        ) : issues.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Alles in Ordnung!</h3>
              <p className="text-slate-600">
                Alle Artikel haben vollständige Informationen.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Info Box */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-orange-900 mb-1">Fehlende Informationen</h3>
                  <p className="text-sm text-orange-700">
                    Die folgenden Artikel haben unvollständige Daten. Bitte vervollständigen Sie die Informationen für eine bessere Artikelverwaltung.
                  </p>
                </div>
              </div>
            </div>

            {/* Issues Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Artikelname</TableHead>
                    <TableHead className="font-semibold text-slate-700">Artikelnummer</TableHead>
                    <TableHead className="font-semibold text-slate-700">Probleme</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issues.map((issue) => (
                    <TableRow key={issue.article_id} className="hover:bg-slate-50">
                      <TableCell className="font-medium text-slate-900">{issue.article_name}</TableCell>
                      <TableCell className="text-slate-600">
                        {issue.article_number || <span className="text-slate-400 italic">nicht angegeben</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {issue.issues.map((problem, idx) => (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className={`${getIssueColor(issue.issues.length)} text-xs`}
                            >
                              {problem}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {user?.role === "admin" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/articles/edit/${issue.article_id}`)}
                            data-testid={`edit-article-${issue.article_id}`}
                            className="border-slate-300"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Summary */}
            <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-3">Zusammenfassung</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: "Keine Bilder", count: issues.filter(i => i.issues.some(p => p.includes("Bilder"))).length },
                  { label: "Artikelnummer fehlt", count: issues.filter(i => i.issues.some(p => p.includes("Artikelnummer"))).length },
                  { label: "Preis fehlt", count: issues.filter(i => i.issues.some(p => p.includes("Preis"))).length },
                  { label: "Gewicht fehlt", count: issues.filter(i => i.issues.some(p => p.includes("Gewicht"))).length },
                  { label: "Kategorie fehlt", count: issues.filter(i => i.issues.some(p => p.includes("Kategorie"))).length },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-3 bg-slate-50 rounded-lg">
                    <div className="text-2xl font-bold text-slate-900">{stat.count}</div>
                    <div className="text-xs text-slate-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
